/**
 * ExecuteShelf — impl_tasks / change_requests / impediments.
 *
 * Phase 6 §"6-shelf reserve". Round 6 vocabulary discipline.
 */

import { ShelfCard } from "./ShelfCard";
import type { ShelfLabelResolver } from "./types";
import { IDENTITY_SHELF_LABEL } from "./types";

interface Props {
  projectId?: string | null;
  /** Template-layer vocabulary SKIN resolver (supplied by ShelfSidebar; one
   *  deduped fetch). Entity-type cards skin; Impediments/Pipeline (workflow
   *  surfaces) stay verbatim. Defaults to canonical. */
  label?: ShelfLabelResolver;
}

export function ExecuteShelf({ projectId, label = IDENTITY_SHELF_LABEL }: Props) {
  const base = projectId ? `/projects/${projectId}` : "";
  return (
    <div
      data-testid="execute-shelf"
      role="region"
      aria-label="Execute"
      className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2"
    >
      <ShelfCard
        to={`${base}/tasks`}
        label={label("impl_task", { plural: true, fallback: "Tasks" })}
        testId="execute-tasks"
      />
      <ShelfCard
        to={`${base}/change-control`}
        label={label("change_request", { plural: true, fallback: "Change Requests" })}
        testId="execute-change-requests"
      />
      {/* The page exists — it is called risks-and-blockers. This card said
          /impediments, which is routed nowhere, so it was marked unavailable
          rather than repointed: a working surface presented as unbuilt. */}
      <ShelfCard
        to={`${base}/risks-and-blockers`}
        label="Impediments"
        testId="execute-impediments"
      />
      {/* Meetings and Action Items had no card, so the rail could not reach
          them and a tour that opened them left the new rail (founder
          6428293584 (B)). */}
      <ShelfCard
        to={`${base}/meetings`}
        label="Meetings"
        testId="execute-meetings"
      />
      <ShelfCard
        to={`${base}/action-items`}
        label="Action Items"
        testId="execute-action-items"
      />
      {/* The Development Pipeline card is WITHDRAWN, not hidden.
          `advance_stage` has zero callers, so a started pipeline can never
          leave BASELINE — and there is no advance, cancel or delete route, no
          caller for `record_stage_error` (the only writer of FAILED), and
          Restart is gated on COMPLETED/FAILED. So the FIRST press wedges that
          project's pipeline page permanently. 0 of 6,073 workflow_runs are
          pipelines, so nobody is in that state yet.
          See row 4508261a; the backend service and routes are left in place. */}
    </div>
  );
}
